import { useState, useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { useUser } from "~/context/userContext";
import firebase from "~/firebase/clientApp";
import {
  Box,
  Skeleton,
  Stack,
  Center,
  useToast,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiPlusCircle } from "react-icons/fi";
import AnimatedScalingBox from "~/components/animations/AnimatedScalingBox";
import { Display03, Paragraph03, Paragraph04 } from "~/components/Texts";
import { userRoleToString } from "~/utils/Formatters";
import Image from "~/components/Image";

const COLLECTION_USERS = "users";
const COLLECTION_USERS_PRIVATE = "private";
const COLLECTION_SHOPS = "shops";
const DOCUMENT_USERS_PRIVATE_DATA = "data";

export default function MyShops(props) {
  const {
    direction = "row",
    currentShopId,
    onNewShopSelect = () => {},
    onExistingShopSelect = () => {},
    imageSize = "192px",
  } = props;
  const toast = useToast();
  const { user } = useUser();
  const [shops, setShops] = useState([]);
  const [roles, setRoles] = useState(null);
  const [loadingShops, setLoadingShops] = useState(true);

  useEffect(() => {
    const unsubscriber = firebase
      .firestore()
      .collection(COLLECTION_USERS)
      .doc(user.uid)
      .collection(COLLECTION_USERS_PRIVATE)
      .doc(DOCUMENT_USERS_PRIVATE_DATA)
      .onSnapshot(
        function (querySnapshot) {
          // Shops IDs are stored as each role key (and the role value is the type of role: 'owner', 'editor', etc.)
          let shopsIds = Object.keys(querySnapshot.data().roles);
          setRoles(querySnapshot.data().roles);

          return Promise.all(
            shopsIds
              .filter((shopId) => {
                if (currentShopId) return shopId !== currentShopId;
                else return true;
              })
              .map((shopId) => {
                return firebase
                  .firestore()
                  .collection(COLLECTION_SHOPS)
                  .doc(shopId)
                  .get()
                  .then((document) => {
                    return { ...{ id: document.id }, ...document.data() };
                  });
              })
          )
            .then((shops) => {
              // Order alphabetically, otherwise shops get returned in different order each time
              return Promise.resolve(
                shops.sort((shopA, shopB) => {
                  return shopA.displayNames.es.localeCompare(
                    shopB.displayNames.es
                  );
                })
              );
            })
            .then((shops) => setShops(shops))
            .catch((error) => {
              Sentry.captureException(error);
              showErrorMessage();
            })
            .finally(() => setLoadingShops(false));
        },
        function (error) {
          Sentry.captureException(error);
          showErrorMessage();
        }
      );

    // Unsubscribe auth listener on unmount
    return () => unsubscriber();
  }, [currentShopId]);

  function showErrorMessage() {
    toast({
      position: "top",
      title: "Ocurrió un error.",
      description: "No pudimos encontrar tus tiendas en este momento.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <Stack
      direction={direction}
      wrap={true}
      w="100%"
      h="100%"
      spacing={direction === "row" ? "4" : "8"}
      justify="flex-start"
      align={direction === "row" ? "flex-start" : "center"}
      overflow="visible"
    >
      <NewShopButton
        onNewShopSelect={() => onNewShopSelect()}
        imageSize={imageSize}
      />
      {loadingShops && <ShopsSkeletons imageSize={imageSize} />}
      {!loadingShops && (
        <ShopsThumbnails
          shops={shops}
          roles={roles}
          imageSize={imageSize}
          onExistingShopSelect={(shop) => onExistingShopSelect(shop)}
        />
      )}
    </Stack>
  );
}

function NewShopButton(props) {
  const { onNewShopSelect, imageSize } = props;

  return (
    <VStack>
      <AnimatedScalingBox
        cursor="pointer"
        onClick={() => onNewShopSelect()}
        boxSize={imageSize}
        p="4"
        borderRadius="brand"
        backgroundColor={useColorModeValue(
          "light.background.action",
          "dark.background.action"
        )}
      >
        <Center w="100%" h="100%">
          <FiPlusCircle size="32px" />
        </Center>
      </AnimatedScalingBox>
      <Paragraph03>
        <b>Nueva tienda</b>
      </Paragraph03>
    </VStack>
  );
}

function ShopsThumbnails(props) {
  const { shops, roles, onExistingShopSelect, imageSize } = props;

  return shops.map((shop, index) => {
    return (
      <VStack key={`myShop#${index}`}>
        <AnimatedScalingBox
          boxSize={imageSize}
          borderRadius="brand"
          overflow="hidden"
          backgroundColor={useColorModeValue(
            "light.background.strong",
            "dark.background.strong"
          )}
          cursor="pointer"
          onClick={() => onExistingShopSelect(shop)}
        >
          {shop.logo && (
            <Image
              src={shop.logo.src}
              width={imageSize}
              height={imageSize}
              objectFit={shop.logo.scaleType}
            />
          )}
          {!shop.logo && (
            <Center w="100%" h="100%">
              <Display03>{shop.displayNames.es[0]}</Display03>
            </Center>
          )}
        </AnimatedScalingBox>
        <Paragraph03 maxW="100px" isTruncated>
          {shop.displayNames.es}
        </Paragraph03>
        <Box
          px="4"
          backgroundColor={useColorModeValue(
            "light.background.strong",
            "dark.background.strong"
          )}
          borderRadius="full"
        >
          <Paragraph04>{userRoleToString(roles[shop.id])}</Paragraph04>
        </Box>
      </VStack>
    );
  });
}

function ShopsSkeletons({ imageSize }) {
  return [1, 2, 3].map((item) => {
    return (
      <Skeleton
        key={`myShopSkeleton#${item}`}
        height={imageSize}
        width={imageSize}
        borderRadius="brand"
        backgroundColor={useColorModeValue(
          "light.background.strong",
          "dark.background.strong"
        )}
        cursor="pointer"
      />
    );
  });
}
